/**
 * OptimizationStatusAlert
 *
 * Displays the current optimization status with:
 * - Status text with appropriate icons
 * - Loading spinner when running
 * - Last run timestamp
 * - Infeasibility analysis with "Find Minimum Limit" feature
 */

import React from 'react';
import {
    Alert,
    Box,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

/**
 * @param {Object} props
 * @param {Object} props.optimization - Optimization state from useOptimization hook
 * @param {Date|null} props.lastRunTimestamp - Timestamp of last completed run
 * @param {boolean} props.findingMinimumLimit - Whether minimum limit search is in progress
 * @param {Object|null} props.minimumLimitResult - Result from minimum limit search
 * @param {Function} props.onFindMinimumLimit - Callback to start minimum limit search
 * @param {Function} props.onApplyMinimumLimit - Callback to apply found minimum limit
 * @param {Function} props.onClearMinimumResult - Callback to clear minimum limit result
 */
export default function OptimizationStatusAlert({
    optimization,
    lastRunTimestamp,
    findingMinimumLimit,
    minimumLimitResult,
    onFindMinimumLimit,
    onApplyMinimumLimit,
    onClearMinimumResult,
}) {
    if (!optimization.status) return null;

    const isError = optimization.status.includes('Error') ||
        optimization.status.includes('Failed') ||
        optimization.status.includes('infeasible');
    const isRunning = optimization.status === 'Running';
    const isQueued = optimization.status === 'Queued';

    // Determine status display text
    const getStatusText = () => {
        if (isRunning && optimization.statusPhase) {
            return `⚡ ${optimization.statusPhase}`;
        }
        if (isRunning) {
            return '⚡ Optimization Running...';
        }
        if (isQueued) {
            return '⏳ Queued - waiting to start...';
        }
        if (['optimal', 'completed', 'Success', 'time_limit'].includes(optimization.status)) {
            return '✓ Optimization Complete';
        }
        if (optimization.status === 'Loaded from save') {
            return '✓ Results Loaded';
        }
        if (optimization.status === 'Submitting...' || optimization.status === 'Submitted - polling for results...') {
            return `⚡ ${optimization.status}`;
        }
        if (optimization.status?.startsWith('Failed') || optimization.status?.startsWith('Error')) {
            return `❌ ${optimization.status}`;
        }
        return `Status: ${optimization.status || 'Ready'}`;
    };

    const getStatusColor = () => {
        if (isRunning || isQueued) return '#3498db';
        return vortxColors.gold;
    };

    return (
        <Alert
            severity={isError ? 'error' : 'info'}
            icon={false}
            sx={{ mb: 2 }}
        >
            <style>
                {`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
                  .cursor-blink { animation: blink 1s step-end infinite; }`}
            </style>

            {/* Status Display */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isRunning && (
                    <CircularProgress size={16} sx={{ color: vortxColors.gold }} />
                )}
                <Typography
                    component="span"
                    sx={{
                        fontFamily: vortxTypography.mono,
                        color: getStatusColor(),
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        letterSpacing: '0.03em'
                    }}
                >
                    {getStatusText()}
                    {(isRunning || isQueued) && <span className="cursor-blink">_</span>}
                </Typography>

                {/* Timestamp when results available and not running */}
                {lastRunTimestamp && optimization.results && !isRunning && (
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: vortxTypography.mono,
                            color: 'text.secondary',
                            fontSize: '0.8rem',
                            ml: 2
                        }}
                    >
                        • Last run: {lastRunTimestamp.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        })}
                    </Typography>
                )}
            </Box>

            {/* Finding Minimum Limit Progress */}
            {findingMinimumLimit && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <CircularProgress size={24} sx={{ color: vortxColors.gold }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Finding minimum import limit...
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Running binary search optimization (may take 2-5 minutes)
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Find Minimum Limit Button */}
            {optimization.infeasibilityMetrics?.canFindMinimum &&
                !minimumLimitResult &&
                !findingMinimumLimit && (
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onFindMinimumLimit}
                            sx={{
                                backgroundColor: vortxColors.gold,
                                color: '#000',
                                fontWeight: 600,
                                '&:hover': { backgroundColor: vortxColors.goldDark || '#c9a227' }
                            }}
                        >
                            Find Minimum Limit
                        </Button>
                    </Box>
                )}

            {/* Minimum Limit Result */}
            {minimumLimitResult && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: minimumLimitResult.error ? 'error.light' : 'success.light',
                    borderRadius: 1
                }}>
                    {minimumLimitResult.error ? (
                        <Typography variant="body2" color="error.dark">
                            Error: {minimumLimitResult.error}
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                                Minimum feasible limit: {minimumLimitResult.minimum_import_limit_kw?.toLocaleString()} kW
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'success.dark', mt: 0.5 }}>
                                Recommended with 5% margin: {minimumLimitResult.minimum_with_margin_kw?.toLocaleString()} kW
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => {
                                    onApplyMinimumLimit(minimumLimitResult.minimum_with_margin_kw);
                                    onClearMinimumResult();
                                }}
                                sx={{ mt: 1, color: 'success.dark' }}
                            >
                                Apply {minimumLimitResult.minimum_with_margin_kw} kW
                            </Button>
                        </>
                    )}
                </Box>
            )}
        </Alert>
    );
}
